import { call, put, takeLeading } from 'redux-saga/effects';

import landmarkApi from 'api/landmarkApi';
import { LANDMARK_LIST } from 'constants/index';
import { apiErrorHandler } from 'utils';

import {
  actionCreateLandmarkFailed,
  actionCreateLandmarkSuccess,
  actionDeleteLandmarkFailed,
  actionDeleteLandmarkSuccess,
  actionGetLandmarkDetailsFailed,
  actionGetLandmarkDetailsSuccess,
  actionGetLandmarkListFailed,
  actionGetLandmarkListSuccess,
  actionUpdateLandmarkFailed,
  actionUpdateLandmarkSuccess,
} from './action';
import {
  CREATE_LANDMARK,
  DELETE_LANDMARK,
  GET_LANDMARK_DETAILS,
  GET_LANDMARK_LIST,
  UPDATE_LANDMARK,
} from './actionTypes';

const mapLandmarkOptions = (data, marketName) => {
  if (!Array.isArray(data) || !data.length) {
    return [];
  }

  const firstItem = data[0];

  if (firstItem && !firstItem.landmarks) {
    return [
      {
        label: marketName || firstItem.market?.name || firstItem.market?.nameEN || '',
        options: data.map((item) => ({
          value: item._id || item.id,
          label: item.name || item.nameEN || '',
          group: item.market?._id || item.market?.id || '',
        })),
      },
    ].filter((group) => group.options.length > 0);
  }

  return data
    .map((item) => ({
      label: marketName || item.destination || '',
      options: Object.keys(item.landmarks || {}).map((key) => ({
        value: key,
        label: LANDMARK_LIST.find((landmark) => landmark.value === key)?.label || key,
        group: item.destination,
      })),
    }))
    .filter((group) => group.options.length > 0);
};

function* getLandmarkList({ payload }) {
  const { destination, market, marketName } = payload || {};

  try {
    const response = yield call(landmarkApi.getLandmarks, {
      destination,
      market,
    });
    const landmarkOptions = mapLandmarkOptions(
      response.payload?.data,
      marketName,
    );

    yield put(actionGetLandmarkListSuccess({ landmarkOptions }));
  } catch (error) {
    apiErrorHandler(error);

    yield put(actionGetLandmarkListFailed());
  }
}

function* createLandmark({ payload }) {
  try {
    const result = yield call(landmarkApi.createLandmark, payload);

    if (payload?.callback) {
      payload.callback(result);
    }

    yield put(actionCreateLandmarkSuccess(result));
  } catch (error) {
    apiErrorHandler(error);

    yield put(actionCreateLandmarkFailed(error));
  }
}

function* getLandmarkDetails({ payload }) {
  try {
    const { id, callback } = payload || {};
    const result = yield call(landmarkApi.getLandmarkDetails, id);

    if (callback) {
      callback(result);
    }

    yield put(actionGetLandmarkDetailsSuccess(result?.payload || result));
  } catch (error) {
    apiErrorHandler(error);
    payload?.errorCallback?.(error);
    yield put(actionGetLandmarkDetailsFailed(error));
  }
}

function* updateLandmark({ payload }) {
  try {
    const { id, callback, ...updatePayload } = payload || {};
    const result = yield call(landmarkApi.updateLandmark, id, updatePayload);

    if (callback) {
      callback(result);
    }

    yield put(actionUpdateLandmarkSuccess(result));
  } catch (error) {
    apiErrorHandler(error);
    yield put(actionUpdateLandmarkFailed(error));
  }
}

function* deleteLandmark({ payload }) {
  try {
    const { id, callback } = payload || {};
    const result = yield call(landmarkApi.deleteLandmark, id);

    if (callback) {
      callback(result);
    }

    yield put(actionDeleteLandmarkSuccess(result));
  } catch (error) {
    apiErrorHandler(error);
    yield put(actionDeleteLandmarkFailed(error));
  }
}

export default function* landmarkSaga() {
  yield takeLeading(GET_LANDMARK_LIST, getLandmarkList);
  yield takeLeading(GET_LANDMARK_DETAILS, getLandmarkDetails);
  yield takeLeading(CREATE_LANDMARK, createLandmark);
  yield takeLeading(UPDATE_LANDMARK, updateLandmark);
  yield takeLeading(DELETE_LANDMARK, deleteLandmark);
}
